import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import ZazuLogo from '@/components/ZazuLogo';
import LanguageSelector from '@/components/LanguageSelector';
import ClearCacheButton from '@/components/ClearCacheButton';
import MathCaptcha from '@/components/MathCaptcha';
import { toast } from 'sonner';
import axios from 'axios';
import { Eye, EyeOff, Loader2, User, Users, GraduationCap, Image as ImageIcon, ArrowLeft, X } from 'lucide-react';

// Build version: 2026-02-25-v3 - Google login removed
const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [studentCode, setStudentCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginType, setLoginType] = useState('parent');
  const [captcha, setCaptcha] = useState({ verified: false });
  const [totpChallenge, setTotpChallenge] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const { login, verifyTotpLogin, studentLogin, api, logout, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // If we got here via ProtectedRoute?reauth=1 (a student session tried to open an
  // admin/employee surface), sign the student out and prompt for a real login.
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('reauth') === '1') {
      if (user?.account_type === 'student') {
        try { logout(); } catch (err) { void err; }
      }
      toast.info('Please sign in with your admin or employee account to access that area.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Picture Login state
  const [studentMode, setStudentMode] = useState('picture'); // 'picture' | 'code'
  const [pictureStep, setPictureStep] = useState('email'); // 'email' | 'chooser'
  const [pictureEmail, setPictureEmail] = useState('');
  const [pictureChildren, setPictureChildren] = useState([]);
  const [pictureLoading, setPictureLoading] = useState(false);

  // On mount, hydrate remembered email so parents don't have to type it every time
  useEffect(() => {
    const remembered = localStorage.getItem('zazu_remembered_parent_email');
    if (remembered) {
      setPictureEmail(remembered);
      // Auto-fetch children when we already know the email
      fetchPictureChildren(remembered, /*silent*/ true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPictureChildren = async (targetEmail, silent = false) => {
    const clean = (targetEmail || '').trim().toLowerCase();
    if (!clean) {
      if (!silent) toast.error('Please enter an email');
      return;
    }
    setPictureLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/children-by-email`, { email: clean });
      const kids = res.data?.children || [];
      if (kids.length === 0) {
        if (!silent) toast.error("We couldn't find any learners for that email. Ask your parent to sign in once first.");
        return;
      }
      setPictureChildren(kids);
      setPictureStep('chooser');
      localStorage.setItem('zazu_remembered_parent_email', clean);
      setPictureEmail(clean);
    } catch (err) {
      if (err.response?.status === 429) {
        const detail = err.response?.data?.detail || 'Too many attempts. Please try again in a minute.';
        if (!silent) toast.error(detail);
      } else if (!silent) {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setPictureLoading(false);
    }
  };

  const handlePictureEmailSubmit = (e) => {
    e.preventDefault();
    fetchPictureChildren(pictureEmail);
  };

  const handlePictureChildTap = async (child) => {
    setPictureLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/picture-login`, {
        email: pictureEmail,
        child_id: child.id,
      });
      const { access_token, user, child_id } = res.data;
      // Persist auth token where AuthContext expects it
      localStorage.setItem('littlelearners-token', access_token);
      localStorage.setItem('token', access_token);
      localStorage.setItem('littlelearners-user', JSON.stringify(user));
      localStorage.setItem('zazu_active_child_id', child_id);
      toast.success(`Welcome, ${child.name}!`);
      // Deep-link straight into the child's learning hub
      window.location.href = `/learn/${child_id}`;
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error(err.response?.data?.detail || 'Too many attempts. Please wait a minute and try again.');
      } else {
        toast.error(err.response?.data?.detail || "Couldn't log in. Try again.");
      }
    } finally {
      setPictureLoading(false);
    }
  };

  const forgetRememberedEmail = () => {
    localStorage.removeItem('zazu_remembered_parent_email');
    setPictureEmail('');
    setPictureChildren([]);
    setPictureStep('email');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captcha.verified) {
      toast.error('Please solve the captcha before signing in.');
      return;
    }
    setLoading(true);
    
    try {
      const userData = await login(email, password);
      // If the account has 2FA enabled, `login()` returns a challenge object
      // instead of the user. Prompt for the TOTP code inline.
      if (userData && userData.needsTotp) {
        setTotpChallenge(userData.challenge);
        toast.info('Enter the code from your authenticator app.');
        return;
      }
      // Persist parent's email for the picture-login flow next time
      try { localStorage.setItem('zazu_remembered_parent_email', email.trim().toLowerCase()); } catch {}
      toast.success('Welcome back!');
      // Route staff/interns to their own dashboard
      if (userData?.is_staff && userData?.intern_role) {
        navigate('/intern/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Student login with code
      const response = await studentLogin(studentCode);
      toast.success(`Welcome, ${response.child_name}!`);
      navigate('/student-dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Student code not found. Please check and try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex flex-col">
      {/* Header */}
      <header className="p-4 flex items-center justify-between">
        <Link to="/" data-testid="login-logo-link">
          <ZazuLogo size="md" />
        </Link>
        <LanguageSelector variant="compact" className="border-slate-200" />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-fredoka text-3xl text-slate-900">{t('auth.login_title')}</CardTitle>
            <CardDescription className="text-base">{t('auth.login_subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={loginType} onValueChange={setLoginType} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="parent" className="flex items-center gap-2" data-testid="parent-login-tab">
                  <Users className="w-4 h-4" /> Parent/Teacher
                </TabsTrigger>
                <TabsTrigger value="student" className="flex items-center gap-2" data-testid="student-login-tab">
                  <GraduationCap className="w-4 h-4" /> Student
                </TabsTrigger>
              </TabsList>

              <TabsContent value="parent">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('auth.email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('auth.email')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="login-email-input"
                      className="h-12"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">{t('auth.password')}</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        data-testid="login-password-input"
                        className="h-12 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <MathCaptcha onChange={setCaptcha} />

                  {totpChallenge && (
                    <div className="p-3 rounded-lg border-2 border-indigo-200 bg-indigo-50 space-y-2" data-testid="totp-prompt">
                      <p className="text-sm font-bold text-indigo-800">Two-factor code required</p>
                      <p className="text-xs text-indigo-700">Enter the 6-digit code from your authenticator app (or a backup code).</p>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={totpCode}
                          onChange={(e) => setTotpCode(e.target.value)}
                          placeholder="123456"
                          className="font-mono text-center tracking-widest"
                          maxLength={9}
                          data-testid="totp-input"
                        />
                        <Button
                          type="button"
                          onClick={async () => {
                            if (!totpCode.trim()) return;
                            setLoading(true);
                            try {
                              const userData = await verifyTotpLogin(totpChallenge, totpCode);
                              toast.success('Welcome back!');
                              if (userData?.is_staff && userData?.intern_role) navigate('/intern/dashboard');
                              else navigate('/dashboard');
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Invalid code');
                            } finally {
                              setLoading(false);
                            }
                          }}
                          disabled={loading || !totpCode.trim()}
                          data-testid="totp-verify-btn"
                        >
                          Verify
                        </Button>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-12 text-lg bg-emerald-500 hover:bg-emerald-600"
                    disabled={loading || !captcha.verified || !!totpChallenge}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      t('auth.login_button')
                    )}
                  </Button>

                </form>
              </TabsContent>

              <TabsContent value="student">
                {/* Sub-mode toggle: Picture Login vs Code Login */}
                <div className="grid grid-cols-2 gap-2 mb-5 p-1 bg-slate-100 rounded-xl">
                  <button
                    type="button"
                    onClick={() => setStudentMode('picture')}
                    className={`h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${studentMode === 'picture' ? 'bg-white shadow-sm text-emerald-700' : 'text-slate-600 hover:text-slate-800'}`}
                    data-testid="student-mode-picture"
                  >
                    <ImageIcon className="w-4 h-4" /> Picture Login
                  </button>
                  <button
                    type="button"
                    onClick={() => setStudentMode('code')}
                    className={`h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${studentMode === 'code' ? 'bg-white shadow-sm text-emerald-700' : 'text-slate-600 hover:text-slate-800'}`}
                    data-testid="student-mode-code"
                  >
                    <GraduationCap className="w-4 h-4" /> Student Code
                  </button>
                </div>

                {/* Picture Login Mode */}
                {studentMode === 'picture' && pictureStep === 'email' && (
                  <form onSubmit={handlePictureEmailSubmit} className="space-y-4">
                    <div className="text-center mb-3">
                      <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-2">
                        <ImageIcon className="w-8 h-8 text-white" />
                      </div>
                      <p className="text-slate-700 font-semibold">Who's learning today?</p>
                      <p className="text-slate-500 text-sm">Ask a parent to enter their email once. Kids can then pick their picture to sign in.</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pictureEmail">Parent's Email</Label>
                      <Input
                        id="pictureEmail"
                        type="email"
                        placeholder="parent@example.com"
                        value={pictureEmail}
                        onChange={(e) => setPictureEmail(e.target.value)}
                        required
                        data-testid="picture-login-email-input"
                        className="h-12"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-12 text-lg bg-emerald-500 hover:bg-emerald-600"
                      disabled={pictureLoading || !pictureEmail}
                      data-testid="picture-login-fetch-btn"
                    >
                      {pictureLoading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
                      Show my picture
                    </Button>
                  </form>
                )}

                {studentMode === 'picture' && pictureStep === 'chooser' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <button
                        type="button"
                        onClick={() => setPictureStep('email')}
                        className="text-slate-500 hover:text-slate-700 flex items-center gap-1 text-sm"
                        data-testid="picture-login-back"
                      >
                        <ArrowLeft className="w-4 h-4" /> Change email
                      </button>
                      <button
                        type="button"
                        onClick={forgetRememberedEmail}
                        className="text-slate-400 hover:text-slate-600 flex items-center gap-1 text-xs"
                        data-testid="picture-login-forget"
                      >
                        <X className="w-3 h-3" /> Not your device?
                      </button>
                    </div>
                    <p className="text-center text-slate-600 text-sm mb-4">
                      Tap your picture to start learning, <span className="font-semibold">{pictureEmail.split('@')[0]}</span>'s family!
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {pictureChildren.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => handlePictureChildTap(c)}
                          disabled={pictureLoading}
                          className="group relative flex flex-col items-center gap-2 p-3 rounded-2xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all disabled:opacity-50"
                          data-testid={`picture-child-${c.id}`}
                        >
                          <div
                            className="w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold text-white shadow-md group-hover:scale-105 transition-transform"
                            style={{ backgroundColor: c.avatar_color || '#10B981' }}
                          >
                            {(c.avatar && c.avatar.length <= 2) ? c.avatar : (c.name || 'L')[0].toUpperCase()}
                          </div>
                          <div className="text-sm font-semibold text-slate-800 truncate max-w-full">{c.name}</div>
                        </button>
                      ))}
                    </div>
                    {pictureLoading && (
                      <div className="mt-4 flex items-center justify-center text-slate-500 text-sm">
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing you in…
                      </div>
                    )}
                  </div>
                )}

                {/* Code Login Mode (legacy) */}
                {studentMode === 'code' && (
                  <form onSubmit={handleStudentLogin} className="space-y-4">
                    <div className="text-center mb-4">
                      <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <User className="w-10 h-10 text-white" />
                      </div>
                      <p className="text-slate-600 text-sm">
                        Enter your student code to start learning!
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="studentCode">Student Code</Label>
                      <Input
                        id="studentCode"
                        type="text"
                        placeholder="Enter your code (e.g., ZAZU-1234)"
                        value={studentCode}
                        onChange={(e) => setStudentCode(e.target.value.toUpperCase())}
                        required
                        data-testid="student-code-input"
                        className="h-14 text-center text-xl font-mono tracking-wider"
                        maxLength={10}
                      />
                      <p className="text-xs text-slate-500 text-center">
                        Ask your parent or teacher for your student code
                      </p>
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-12 text-lg bg-teal-500 hover:bg-teal-600"
                      disabled={loading || studentCode.length < 4}
                      data-testid="student-login-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Finding your profile...
                        </>
                      ) : (
                        "Let's Go!"
                      )}
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>

            <div className="mt-6 text-center">
              <p className="text-slate-600">
                {t('auth.no_account')}{' '}
                <Link to="/register" className="text-emerald-500 hover:text-emerald-600 font-semibold" data-testid="go-to-register-link">
                  {t('auth.register_button')}
                </Link>
              </p>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col items-center gap-1">
              <p className="text-xs text-slate-400 text-center">
                Login page acting stale or won&apos;t accept your password?
              </p>
              <ClearCacheButton
                testId="login-clear-cache-btn"
                variant="ghost"
                size="sm"
                label="Clear cache & refresh"
                className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
              />
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LoginPage;
